import { PlusIcon, XIcon } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";

const REGIONS = ["European Union", "United States", "United Kingdom", "ASEAN", "Japan", "South Korea", "China", "India", "Canada", "Australia"];
const STATUS = ["Active", "Restricted", "Banned"];

const EMPTY_ROW = {
  region: "United States", status: "Active", prohibited: false,
  usageRestriction: "", areaRestriction: "", importantDate: "",
  minPercent: "", maxPercent: "", note: "",
};

export default function RegionsEditor({ value = [], onChange }) {
  const add = () => onChange([...(value || []), { ...EMPTY_ROW }]);
  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));
  const set = (i, k, v) => {
    const next = [...value]; next[i] = { ...next[i], [k]: v }; onChange(next);
  };

  const inpCls = "rounded-none h-9 font-mono text-sm";

  return (
    <div className="space-y-4" data-testid="regions-editor">
      {(value || []).map((row, i) => (
        <div key={i} className="border border-[var(--border)] p-4 bg-white" data-testid={`region-row-${i}`}>
          <div className="flex items-center justify-between mb-3">
            <span className="label-kicker">Region #{i + 1}</span>
            <button type="button" onClick={() => remove(i)} className="text-[var(--text-muted)] hover:text-[var(--danger)] flex items-center gap-1 text-[11px] font-mono uppercase tracking-[0.14em]" data-testid={`region-remove-${i}`}>
              <XIcon size={12} /> Remove
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="label-kicker">Region</label>
              <Select value={row.region} onValueChange={(v) => set(i, "region", v)}>
                <SelectTrigger className={inpCls} data-testid={`region-${i}-region`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {REGIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="label-kicker">Status</label>
              <Select value={row.status} onValueChange={(v) => set(i, "status", v)}>
                <SelectTrigger className={inpCls} data-testid={`region-${i}-status`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-3">
              <div>
                <label className="label-kicker block">Prohibited</label>
                <div className="mt-2 flex items-center gap-2">
                  <Switch checked={!!row.prohibited} onCheckedChange={(v) => set(i, "prohibited", v)} data-testid={`region-${i}-prohibited`} />
                  <span className="font-mono text-xs">{row.prohibited ? "YES" : "No"}</span>
                </div>
              </div>
            </div>
            <div>
              <label className="label-kicker">Important Date</label>
              <Input value={row.importantDate || ""} onChange={(e) => set(i, "importantDate", e.target.value)} placeholder="DD/MM/YYYY" className={inpCls} data-testid={`region-${i}-date`} />
            </div>
            <div>
              <label className="label-kicker">Min %</label>
              <Input value={row.minPercent || ""} onChange={(e) => set(i, "minPercent", e.target.value)} className={inpCls} data-testid={`region-${i}-min`} />
            </div>
            <div>
              <label className="label-kicker">Max %</label>
              <Input value={row.maxPercent || ""} onChange={(e) => set(i, "maxPercent", e.target.value)} className={inpCls} data-testid={`region-${i}-max`} />
            </div>
            <div className="md:col-span-3">
              <label className="label-kicker">Usage Restriction</label>
              <Textarea value={row.usageRestriction || ""} onChange={(e) => set(i, "usageRestriction", e.target.value)} rows={2} className="rounded-none text-sm" data-testid={`region-${i}-restriction`} />
            </div>
            <div className="md:col-span-3">
              <label className="label-kicker">Note</label>
              <Textarea value={row.note || ""} onChange={(e) => set(i, "note", e.target.value)} rows={2} className="rounded-none text-sm" data-testid={`region-${i}-note`} />
            </div>
          </div>
        </div>
      ))}
      <Button type="button" variant="outline" onClick={add} className="rounded-none h-9 font-mono text-[11px] uppercase tracking-[0.18em]" data-testid="region-add">
        <PlusIcon size={14} className="mr-2" /> Add Region
      </Button>
    </div>
  );
}
